
import { useCallback, useEffect, useRef } from 'react';
import { Button, EmptyState, BrandLoading } from '@scripsteam/scrips-signal-ds';
import { useNotifications, flattenNotifications } from '@/notifications/services/use-notifications';
import { useResetNotificationSeen } from '@/notifications/services/use-reset-notification-seen';
import { useMarkNotificationsRead } from '@/notifications/services/use-mark-notifications-read';
import { NotificationRow } from './notification-row';

const SCROLL_THRESHOLD_PX = 120;

export function NotificationPanel({ onClose }: { onClose: () => void }) {
  const { data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useNotifications({});
  const resetSeen = useResetNotificationSeen();
  const markRead = useMarkNotificationsRead();

  const notifications = flattenNotifications(data?.pages);

  const scrollRef = useRef<HTMLDivElement>(null);
 const viewedUnreadRef = useRef<Set<string>>(new Set());
  const onRowViewed = useCallback((id: string) => {
    viewedUnreadRef.current.add(id);
  }, []);
 const markReadRef = useRef(markRead.mutate);
  useEffect(() => {
    markReadRef.current = markRead.mutate;
  });

 const didReset = useRef(false);
  useEffect(() => {
    if (didReset.current) return;
    didReset.current = true;
    resetSeen.mutate();
  }, [resetSeen]);

useEffect(() => {
    const viewed = viewedUnreadRef.current;
    return () => {
      const ids = [...viewed];
      if (ids.length > 0) markReadRef.current(ids);
    };
  }, []);

  function onScroll(e: React.UIEvent<HTMLDivElement>) {
    const el = e.currentTarget;
    if (
      hasNextPage &&
      !isFetchingNextPage &&
      el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD_PX
    ) {
      void fetchNextPage();
    }
  }

  return (
 <div className="flex flex-col min-h-0 overflow-hidden">
      {/* Showcase look (design NotificationPanel `.head` / `.title` 16/24/600): `.notification-panel-*`. */}
      <div className="notification-panel-head">
        <span className="notification-panel-title">Notifications</span>
        <Button variant="tertiary" size="sm" onClick={onClose}>
          Close
        </Button>
      </div>

      <div ref={scrollRef} className="notification-panel-body" onScroll={onScroll}>
        {isLoading ? (
          <BrandLoading size={48} label="Loading notifications…" />
        ) : isError ? (
       <EmptyState
            icon="warning"
            title="Couldn't load notifications."
            description="Something went wrong. Please try again."
            ctaLabel="Retry"
            onCtaClick={() => void refetch()}
          />
        ) : notifications.length === 0 ? (
          <EmptyState icon="bell" title="No notifications" description="You're all caught up." />
        ) : (
          <>
            {notifications.map((n) => (
              <NotificationRow
                key={n.id}
                notification={n}
                scrollRootRef={scrollRef}
                onViewed={onRowViewed}
              />
            ))}
            {isFetchingNextPage && (
              <BrandLoading size={24} label="Loading more…" />
            )}
          </>
        )}
      </div>
    </div>
  );
}
